import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, } from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 24
    },
    title: {
        fontSize: 18,
        marginBottom: 10,
        textAlign: 'center',
        color: '#222'
    },

    metaRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10
    },
    metaText: {
        fontSize: 10,
        color: '#444'
    },

    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        marginTop: 10,
        marginBottom: 6
    },

    table: {
        borderWidth: 1,
        borderColor: '#cfcfcf',
        borderStyle: 'solid',
        borderRadius: 6,
    },
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#d6d6d6',
        borderBottomWidth: 1,
        borderBottomColor: '#cfcfcf',
    },
    bodyRow: {
        flexDirection: 'row'
    },
    rowAlt: {
        backgroundColor: '#f6f6f6'
    },
    rowDivider: {
        borderBottomWidth: 1,
        borderBottomColor: '#eeeeee',
        borderBottomStyle: 'solid'
    },

    col: {
        paddingVertical: 6,
        paddingHorizontal: 6,
        justifyContent: 'center'
    },
    colDivider: {
        borderRightWidth: 1,
        borderRightColor: '#e1e1e1',
        borderRightStyle: 'solid'
    },

    th: {
        fontSize: 8,
        fontWeight: 'bold',
        textAlign: 'center'
    },
    td: {
        fontSize: 8,
        textAlign: 'center',
        lineHeight: 1.2
    },
    tdLeft: {
        textAlign: 'left'
    },
});

/* ============== FECHA / FILE NAME ============== */
const now = new Date();
const headerDate = now.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

/* ============== UTILES ============== */
const money = (n) => {
    const v = Number(n || 0);
    return `$${v.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safe = (x) => String(x ?? '');
const S = (...arr) => arr.filter(Boolean);

/* ============== DOCUMENTO ============== */
const MySuppliesReport = ({ data = [], totals = [], meta = {} }) => {
    const rows = Array.isArray(data) ? data : [];

    const COLS = {
        code: 2.2,
        name: 4.0,
        lot: 1.6,
        ppl: 1.6,
        avail: 1.8,
        lotCost: 2.0,
        unit: 2.2,
        cap: 2.3,
        supplier: 2.7,
        unitCost: 2.0,
        priceMl: 2.0,
        state: 1.6,
    };

    const statusLabel = (isActive) => (isActive ? 'Activa' : 'Inactiva');

    const metaStatus =
        meta?.status === 'active' ? 'Activos' : meta?.status === 'inactive' ? 'Inactivos' : 'Todos';

    const metaQ = (meta?.q || '').trim() ? meta.q : '—';

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <Text style={styles.title}>Reporte de Consumibles</Text>

                <View style={styles.metaRow}>
                    <Text style={styles.metaText}>Fecha: {headerDate}</Text>
                    <Text style={styles.metaText}>Estado: {metaStatus}</Text>
                    <Text style={styles.metaText}>Búsqueda: {metaQ}</Text>
                </View>

                {/* TABLA */}
                <View style={styles.table}>
                    {/* Encabezado */}
                    <View style={styles.headerRow}>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.code }]}><Text style={styles.th}>Clave</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.name }]}><Text style={styles.th}>Nombre</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.lot }]}><Text style={styles.th}>Lote</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.ppl }]}><Text style={styles.th}>Pzas/lote</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.avail }]}><Text style={styles.th}>Pzas disp.</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.lotCost }]}><Text style={styles.th}>Costo/lote</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.unit }]}><Text style={styles.th}>Unidad</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.cap }]}><Text style={styles.th}>Capacidad</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.supplier }]}><Text style={styles.th}>Proveedor</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.unitCost }]}><Text style={styles.th}>Costo/unidad</Text></View>
                        <View style={[styles.col, styles.colDivider, { flex: COLS.priceMl }]}><Text style={styles.th}>Precio/ml</Text></View>
                        <View style={[styles.col, { flex: COLS.state }]}><Text style={styles.th}>Estado</Text></View>
                    </View>

                    {/* Filas */}
                    {rows.map((it, i) => (
                        <View
                            key={it.id ?? i}
                            style={S(styles.bodyRow, i < rows.length - 1 && styles.rowDivider, i % 2 === 1 && styles.rowAlt)}
                        >
                            <View style={[styles.col, styles.colDivider, { flex: COLS.code }]}>
                                <Text style={S(styles.td, styles.tdLeft)}>{safe(it.code)}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.name }]}>
                                <Text style={S(styles.td, styles.tdLeft)}>{safe(it.product_name)}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.lot }]}>
                                <Text style={styles.td}>{Number(it.lot || 0).toLocaleString('es-MX')}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.ppl }]}>
                                <Text style={styles.td}>{Number(it.pieces_per_lot || 0).toLocaleString('es-MX')}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.avail }]}>
                                <Text style={styles.td}>{Number(it.available_pieces || 0).toLocaleString('es-MX')}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.lotCost }]}>
                                <Text style={styles.td}>{money(it.lot_cost)}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.unit }]}>
                                <Text style={S(styles.td, styles.tdLeft)}>
                                    {safe(it.unitofmeasure_label || it.unitOfMeasureLabel || it.unitofmeasure_id || '—')}
                                </Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.cap }]}>
                                <Text style={S(styles.td, styles.tdLeft)}>{safe(it.capacity_dimensions || '—')}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.supplier }]}>
                                <Text style={S(styles.td, styles.tdLeft)}>{safe(it.supplier || '—')}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.unitCost }]}>
                                <Text style={styles.td}>{money(it.unit_cost)}</Text>
                            </View>

                            <View style={[styles.col, styles.colDivider, { flex: COLS.priceMl }]}>
                                <Text style={styles.td}>{money(it.price_per_ml)}</Text>
                            </View>

                            <View style={[styles.col, { flex: COLS.state }]}>
                                <Text style={styles.td}>{statusLabel(!!it.isActive)}</Text>
                            </View>
                        </View>
                    ))}
                </View>

                {/* Totales */}
                {Array.isArray(totals) && totals.length > 0 && (
                    <View style={{ marginTop: 12 }}>
                        <Text style={styles.sectionTitle}>Totales por proveedor (piezas)</Text>

                        <View style={styles.table}>
                            <View style={styles.headerRow}>
                                <View style={[styles.col, styles.colDivider, { flex: 10 }]}><Text style={styles.th}>Proveedor</Text></View>
                                <View style={[styles.col, styles.colDivider, { flex: 4 }]}><Text style={styles.th}># Consumibles</Text></View>
                                <View style={[styles.col, { flex: 6 }]}><Text style={styles.th}>Piezas</Text></View>
                            </View>

                            {totals.map((t, i) => (
                                <View
                                    key={`${t.name}-${i}`}
                                    style={S(styles.bodyRow, i < totals.length - 1 && styles.rowDivider, i % 2 === 1 && styles.rowAlt)}
                                >
                                    <View style={[styles.col, styles.colDivider, { flex: 10 }]}>
                                        <Text style={S(styles.td, styles.tdLeft)}>{safe(t.name || 'Sin proveedor')}</Text>
                                    </View>
                                    <View style={[styles.col, styles.colDivider, { flex: 4 }]}>
                                        <Text style={styles.td}>{Number(t.total_supplies || 0).toLocaleString('es-MX')}</Text>
                                    </View>
                                    <View style={[styles.col, { flex: 6 }]}>
                                        <Text style={styles.td}>{Number(t.total_pieces || 0).toLocaleString('es-MX')}</Text>
                                    </View>
                                </View>
                            ))}
                        </View>
                    </View>
                )}
            </Page>
        </Document>
    );
};
/* --------------------------------------------------- */

/* -------------------- Botón de descarga -------------------- */
const PDFLink = ({ data, folios, foliosDetailed }) => (
    <div style={{ textAlign: 'center', marginTop: '12px' }}>
        <PDFDownloadLink
            document={<MySuppliesReport data={data} folios={folios} foliosDetailed={foliosDetailed} />}
            fileName={`Reporte-Consumibles-${headerDate}.pdf`}
        >
            {({ loading }) => (loading ? 'Generando documento…' : 'Descargar PDF')}
        </PDFDownloadLink>
    </div>
);
/* ----------------------------------------------------------- */

export { PDFLink, MySuppliesReport };